'use client'

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import Image from 'next/image'
import { Maximize2, X } from 'lucide-react'
import { MODEL_OLIVE_COSTS, OLIVE_VALUE_USD, type ModelOliveCost } from '@/types/shared'
import { trackModelShowcaseInteraction } from '@/lib/analytics'

const CDN = 'https://cdn.martini.film/v0.2/assets'
const clip = (id: string, thumbRevision: string, size: string) => ({
  video: `${CDN}/${id}/${id}.mp4`,
  poster: `${CDN}/${id}/thumbs/${thumbRevision}/1080.webp`,
  size,
})

// Three draft/enhance pairs from the board. Every one is the same generation
// rendered twice: identical length, identical frame count, same scene and grade.
//
// They are NOT pixel-identical, and we checked rather than assumed — matched
// frames across three pairs measure 19-23dB luma PSNR where a true upscale
// would sit above 30, while chroma holds at 34-42dB. Enhance is a second
// render, not an upscale: it keeps the shot and regenerates the detail. The
// caption says so, because a reader will notice a prop that moved and conclude
// we swapped the clips on them.
const EXAMPLES = [
  {
    key: 'bartender',
    name: 'Bartender',
    seconds: 10,
    draft: clip(
      'b46ac5b6-a0f7-42f9-bc45-2e1c60e2333f',
      'd3fb71c9d68ba35864cc1afd92a6a7d2e796423b5e2ee45b8c1063d91730cad3',
      '1440 × 608',
    ),
    full: clip(
      '0a910ebf-18b4-460f-9325-5287bf70c30a',
      'b954012bb20eddf2f90d2e6fefbfae1aafeeb309212eef994081941be5451583',
      '2176 × 928',
    ),
  },
  {
    key: 'vault',
    name: 'Vault',
    seconds: 20,
    draft: clip(
      '2f9e376a-cbb5-4a95-9b49-086e9677da66',
      'd4268d660bab83ebe760f0a0a179d69dee604b5c847a23dec8d48f38cbd43f3f',
      '1440 × 608',
    ),
    full: clip(
      'cf89ede3-5c69-4d11-a82e-fb207de6f6fc',
      '68b71b5416068edccd06a084dbb7c33450dfa1cc866ea603c6cc29e57f5f0012',
      '2176 × 928',
    ),
  },
  {
    key: 'graphics',
    name: 'On-screen graphics',
    seconds: 10,
    draft: clip(
      '8322ace8-bff3-48d3-a70f-fcfe52d682fb',
      '1c71d74b5546d461c854e39498a53812cda2896409448552aebcd0f7a9261c03',
      '1440 × 608',
    ),
    full: clip(
      '684ae72a-b521-4911-b9d7-3c38d718c9a3',
      'd525fc2d77b71053236bc2515f4e26947582fecfb0544320de1d42e0b57346c0',
      '2176 × 928',
    ),
  },
] as const

/**
 * Hold two clips in lockstep.
 *
 * `timeupdate` fires ~4x a second and a bare `currentTime =` is a visible seek,
 * so correcting on drift alone both reacts late and looks bad. This corrects
 * every frame instead: a small playbackRate trim (invisible) pulls the enhanced
 * clip back onto the draft's clock, and a seek is only used when drift is
 * already too large to hide.
 *
 * It deliberately does NOT try to manage buffering. An earlier version paused
 * both clips on `waiting` and refused to resume until both reported
 * readyState >= 3 — which deadlocked, because seeking drops readyState back to
 * 1 and a paused video never recovers it. Measured result was both clips paused
 * in 100% of samples, advancing at 8% of real time. Browsers already stall and
 * resume on their own; the only job here is drift.
 */
/** One frame at the 24fps FLUX.3 renders at — the tightest sync worth chasing. */
const FRAME = 1 / 24

function useLockstep(aRef: RefObject<HTMLVideoElement | null>, bRef: RefObject<HTMLVideoElement | null>, key: unknown) {
  useEffect(() => {
    const a = aRef.current
    const b = bRef.current
    if (!key || !a || !b) return

    let raf = 0
    let stopped = false

    const tick = () => {
      if (stopped) return
      // These carry no controls, so a paused clip is always a stall to recover
      // from, never a user's choice. play() on a playing element is a no-op.
      if (a.paused) void a.play().catch(() => {})
      if (b.paused) void b.play().catch(() => {})

      const drift = b.currentTime - a.currentTime
      const mag = Math.abs(drift)
      if (mag > 0.35 && !b.seeking) {
        // Past this a rate trim would take seconds to converge; eat the seek.
        b.currentTime = a.currentTime
        b.playbackRate = 1
      } else if (mag > FRAME) {
        b.playbackRate = drift > 0 ? 0.95 : 1.05
      } else if (b.playbackRate !== 1 && mag < FRAME / 2) {
        // Hysteresis. Correcting to zero leaves the rate trim permanently on,
        // chasing a drift smaller than a single frame — which nobody can see
        // and which never settles.
        b.playbackRate = 1
      }
      raf = requestAnimationFrame(tick)
    }

    a.currentTime = 0
    b.currentTime = 0
    void a.play().catch(() => {})
    void b.play().catch(() => {})
    raf = requestAnimationFrame(tick)

    return () => {
      stopped = true
      cancelAnimationFrame(raf)
      b.playbackRate = 1
    }
  }, [aRef, bRef, key])
}

const enhanceRate = () => {
  const c = MODEL_OLIVE_COSTS['flux-3-draft-enhance'] as ModelOliveCost
  return `$${((c.olivesPerSecond ?? 0) * OLIVE_VALUE_USD).toFixed(2)}/s`
}
const draftRate = () => {
  const c = MODEL_OLIVE_COSTS['flux-3'] as ModelOliveCost
  const olives = c.perSecondQualityTiers?.draft?.olivesPerSecond ?? c.olivesPerSecond ?? 0
  return `$${(olives * OLIVE_VALUE_USD).toFixed(2)}/s`
}

/**
 * Draft next to the enhance of that same generation. Together a pair is ~15MB,
 * so nothing loads until it scrolls into view, and only the selected example is
 * ever mounted. Clicking either pane opens a native <dialog> that stacks them
 * near-full-width — the only size at which a 21:9 pair is worth comparing.
 */
export function Flux3DraftCompare() {
  const wrap = useRef<HTMLDivElement>(null)
  const dialog = useRef<HTMLDialogElement>(null)
  const [started, setStarted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [zoomed, setZoomed] = useState(false)
  const [pick, setPick] = useState(0)
  // `open` is memoised with no deps; a ref keeps it reporting the live example.
  const pickRef = useRef(0)

  const example = EXAMPLES[pick]
  const panes = [
    { key: 'draft', label: 'Draft', rate: draftRate(), ...example.draft },
    { key: 'full', label: 'Enhanced', rate: enhanceRate(), ...example.full },
  ]

  const inlineDraft = useRef<HTMLVideoElement>(null)
  const inlineFull = useRef<HTMLVideoElement>(null)
  const bigDraft = useRef<HTMLVideoElement>(null)
  const bigFull = useRef<HTMLVideoElement>(null)

  // Keyed on the example too, so switching restarts both clips together rather
  // than dropping the new pair in at whatever time the old one had reached.
  useLockstep(inlineDraft, inlineFull, started && visible && `${pick}`)
  useLockstep(bigDraft, bigFull, zoomed && `${pick}`)

  // `started` mounts the sources once and never unmounts them; `visible`
  // gates playback both ways, so scrolling past the section doesn't leave two
  // videos looping (and the lockstep loop un-pausing them) for the rest of the
  // visit. The lockstep hook restarts from 0 on re-entry, which for a
  // comparison is a feature: both clips come back together.
  useEffect(() => {
    const node = wrap.current
    if (!node) return
    const io = new IntersectionObserver(
      entries => {
        const on = entries.some(e => e.isIntersecting)
        setVisible(on)
        if (on) setStarted(true)
      },
      { rootMargin: '200px' },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [])

  // useLockstep only ever plays; pausing on exit is this effect's job.
  useEffect(() => {
    if (visible) return
    inlineDraft.current?.pause()
    inlineFull.current?.pause()
  }, [visible])

  const open = useCallback(() => {
    setStarted(true)
    setZoomed(true)
    dialog.current?.showModal()
    trackModelShowcaseInteraction({
      model: 'flux-3',
      interaction: 'compare_zoomed',
      target: EXAMPLES[pickRef.current].key,
    })
  }, [])

  return (
    <figure ref={wrap} className="mt-12">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {panes.map((p, i) => (
          <div key={p.key}>
            <div className="mb-2.5 flex items-baseline justify-between gap-3">
              <span className={`text-sm font-medium ${p.key === 'full' ? 'text-white' : 'text-white/55'}`}>
                {p.label}
              </span>
              <span className="font-mono text-[11px] text-white/35">
                {p.size} · {p.rate}
              </span>
            </div>
            <button
              type="button"
              onClick={open}
              aria-label={`View ${example.name} draft and enhanced larger`}
              className="group relative block aspect-[21/9] w-full overflow-hidden rounded-xl bg-white/[0.03]"
            >
              <Image
                key={p.poster}
                src={p.poster}
                alt=""
                fill
                unoptimized
                sizes="(min-width: 640px) 50vw, 100vw"
                className="object-cover"
              />
              <video
                key={p.video}
                ref={i === 0 ? inlineDraft : inlineFull}
                src={started ? p.video : undefined}
                preload={started ? 'auto' : 'none'}
                muted
                loop
                playsInline
                aria-label={`${example.name}, ${p.label.toLowerCase()} — a FLUX.3 shot generated in Martini`}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <span className="absolute end-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-lg bg-black/55 opacity-0 backdrop-blur-sm transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
                <Maximize2 strokeWidth={1.75} className="h-4 w-4 text-white" />
              </span>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
        {EXAMPLES.map((e, n) => (
          <button
            key={e.key}
            type="button"
            onClick={() => {
              setPick(n)
              pickRef.current = n
              trackModelShowcaseInteraction({ model: 'flux-3', interaction: 'compare_switched', target: e.key })
            }}
            aria-pressed={n === pick}
            className={`rounded-full border px-4 py-2 text-[13px] font-medium transition-colors ${
              n === pick
                ? 'border-white bg-white text-black'
                : 'border-white/15 bg-white/[0.05] text-white/65 hover:border-white/35 hover:text-white'
            }`}
          >
            {n + 1}. {e.name}
            <span className={`ms-2 font-mono text-[11px] ${n === pick ? 'text-black/45' : 'text-white/30'}`}>
              {e.seconds}s
            </span>
          </button>
        ))}
      </div>

      <figcaption className="mt-6 text-sm leading-relaxed text-white/45">
        One generation, rendered twice. Enhance is a second render rather than an upscale, so expect the same scene,
        framing, length and grade — but look closely and a prop will have shifted. What it buys you is that the full
        rate only ever gets spent on a shot you already picked.{' '}
        <span className="text-white/60">Click either one to see them bigger.</span>
      </figcaption>

      <dialog
        ref={dialog}
        onClose={() => setZoomed(false)}
        onClick={e => {
          if (e.target === dialog.current) dialog.current?.close()
        }}
        aria-label={`${example.name} — draft and enhanced, full screen`}
        className="m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-black/92 backdrop:backdrop-blur-sm"
      >
        <div className="flex h-full w-full flex-col items-center justify-center gap-5 overflow-y-auto p-4 md:p-8">
          {zoomed
            ? panes.map((p, i) => (
                // Two 21:9 panes have to share the viewport, so cap the width by
                // height as well — otherwise the pair scrolls and you can only
                // ever see one of them.
                <div key={p.key} className="w-full max-w-[min(72rem,88vh)]">
                  <div className="mb-2 flex items-baseline justify-between gap-3">
                    <span className={`text-sm font-medium ${p.key === 'full' ? 'text-white' : 'text-white/55'}`}>
                      {p.label}
                    </span>
                    <span className="font-mono text-[11px] text-white/40">
                      {p.size} · {p.rate}
                    </span>
                  </div>
                  <video
                    key={p.video}
                    ref={i === 0 ? bigDraft : bigFull}
                    src={p.video}
                    muted
                    loop
                    playsInline
                    aria-label={`${example.name}, ${p.label.toLowerCase()} — a FLUX.3 shot generated in Martini`}
                    className="aspect-[21/9] w-full rounded-lg bg-black object-cover"
                  />
                </div>
              ))
            : null}
        </div>

        <button
          type="button"
          onClick={() => dialog.current?.close()}
          aria-label="Close"
          className="fixed end-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 md:end-6 md:top-6"
        >
          <X strokeWidth={1.75} className="h-5 w-5" />
        </button>
      </dialog>
    </figure>
  )
}
